import React, { useEffect, useMemo, useState } from 'react';
import { Route, Switch } from 'react-router-dom';
import { Redirect } from 'react-router';
import { connect } from 'react-redux';

import { ThemeProvider } from '@material-ui/core/styles';
import CssBaseline from '@material-ui/core/CssBaseline';

import AppPage from './pages/app';
import {
  LoginPage,
  SignUpPage,
  ForgotPasswordPage,
} from './pages/auth';

import SetPasswordPage from './pages/auth/SetPasswordPage';

import LoadScreen from './components/load-screen';
import Alert from './components/shared/Alert';

import { useAlert } from './hooks';

import { getCurrentUser, setCurrentUser } from './actions/currentUserActions';
import { getCurrentTenant } from './actions/tenantActions';

import { routes } from './constants';
import features from './config/features';

import theme from './styles/theme/theme';
import { defaultStaticColor } from './styles/theme/palette';

const THEME_MODE_STORAGE_KEY = 'caseAppThemeMode';

const getInitialColorMode = () => {
  if (!features.darkMode || typeof window === 'undefined') {
    return 'light';
  }

  const savedMode = window.localStorage.getItem(THEME_MODE_STORAGE_KEY);
  return savedMode === 'dark' ? 'dark' : 'light';
};

const App = (props) => {
  const { getCurrentUser, currentUser, getCurrentTenant, currentTenant, isLoaded } = props;

  const { isOpen, variant, message, closeAlert } = useAlert();
  const [colorMode, setColorMode] = useState(getInitialColorMode);
  const effectiveColorMode = features.darkMode ? colorMode : 'light';

  useEffect(() => {
    onLoad().catch((err) => console.error(err));
  }, []);

  useEffect(() => {
    document.documentElement.setAttribute('data-theme', effectiveColorMode);
    document.body.setAttribute('data-theme', effectiveColorMode);

    // Preserve the previous preference while dark mode is hidden for this release.
    if (features.darkMode) {
      window.localStorage.setItem(THEME_MODE_STORAGE_KEY, effectiveColorMode);
    }
  }, [effectiveColorMode]);

  const onLoad = async () => {
    await getCurrentUser();
    await getCurrentTenant();
  };

  const muiTheme = useMemo(() => {
    return theme(currentTenant.colorPrimary || defaultStaticColor, effectiveColorMode);
  }, [currentTenant.colorPrimary, effectiveColorMode]);

  const toggleColorMode = () => {
    if (!features.darkMode) {
      return;
    }

    setColorMode((mode) => mode === 'dark' ? 'light' : 'dark');
  };

  return (
    <ThemeProvider theme={muiTheme}>
      <CssBaseline />
      <div className='app'>
        {isLoaded ? (
          <>
            {!currentUser.uid ? (
              <Switch>
                <Route exact path={routes.SIGN_IN} component={LoginPage} />
                <Route exact path={routes.SIGN_UP} component={SignUpPage} />
                <Route exact path={routes.FORGOT_PASSWORD} component={ForgotPasswordPage} />
                <Route exact path={routes.SET_PASSWORD} component={SetPasswordPage} />
                <Redirect to={routes.SIGN_IN} />
              </Switch>
            ) : (
              <Switch>
                <Route
                  render={(routeProps) => (
                    <AppPage
                      {...routeProps}
                      colorMode={effectiveColorMode}
                      onColorModeToggle={toggleColorMode}
                    />
                  )}
                />
              </Switch>
            )}
          </>
        ) : (
          <LoadScreen />
        )}
      </div>
      <Alert
        variant={variant}
        message={message}
        open={isOpen}
        onClose={closeAlert}
      />
    </ThemeProvider>
  );
};

const mapStateToProps = (state) => {
  return {
    currentUser: state.user.currentUser,
    currentTenant: state.tenant.currentTenant,
    isLoaded: state.user.isLoaded && state.tenant.isLoaded,
  };
};

const mapDispatchToProps = (dispatch) => {
  return {
    getCurrentUser: () => dispatch(getCurrentUser()),
    setCurrentUser: (user) => dispatch(setCurrentUser(user)),
    getCurrentTenant: () => dispatch(getCurrentTenant())
  };
};

export default connect(mapStateToProps, mapDispatchToProps)(App);
